import { useState, useEffect, useCallback } from "react";
import { DollarSign, TrendingUp, AlertTriangle, Activity, ShieldCheck, Download } from "lucide-react";
import { KPICard } from "@/components/KPICard";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getCashFlowReport, downloadPdfUrl, exportCsvUrl } from "@/lib/api";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const fmt = (n) => {
  if (n === undefined || n === null) return "$0";
  return "$" + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

export default function CashFlowReport({ refreshKey, analysisId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState("detailed");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCashFlowReport(analysisId);
      setReport(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [analysisId]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const handleDownload = () => {
    window.open(downloadPdfUrl("cash-flow", analysisId, template), "_blank");
  };

  const handleCsvExport = () => {
    window.open(exportCsvUrl("cash-flow", analysisId), "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="cash-flow-loading">
        <div className="w-8 h-8 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report || report.total_transactions === 0) {
    return (
      <div className="bg-white border border-[#E7E5DF] rounded-md p-12 text-center" data-testid="cash-flow-empty">
        <Activity className="w-12 h-12 text-[#8A9A86] mx-auto mb-4" />
        <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-2">
          No Cash Flow Data
        </h3>
        <p className="text-[#57534E] text-sm">
          Upload bank statements in the Bank Statements tab to generate your cash flow report.
        </p>
      </div>
    );
  }

  const { averages: avg, monthly_summaries: months, red_flags, trends, detected_loans } = report;

  const chartData = months.map((m) => ({
    month: m.month,
    Revenue: m.true_revenue,
    Expenses: m.total_withdrawals,
    "Net Cash Flow": m.net_cash_flow,
    "Operating CF": m.operating_cash_flow,
  }));

  const expenseData = months.map((m) => ({
    month: m.month,
    Payroll: m.payroll,
    Rent: m.rent,
    Utilities: m.utilities,
    "Loan Payments": m.loan_payments,
    "Owner Draws": m.owner_draws,
    Other: m.other_expenses,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cash-flow-report-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-semibold text-[#1C1917]">
            Cash Flow Report
          </h2>
          <p className="text-sm text-[#57534E] mt-1">
            {report.num_months} months analyzed &middot; {report.total_transactions} transactions
          </p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger data-testid="report-template-select" className="w-[130px] h-9 text-xs border-[#E7E5DF]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="minimal">Minimal</SelectItem>
              <SelectItem value="detailed">Detailed</SelectItem>
              <SelectItem value="executive">Executive</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="download-cf-report-btn"
            onClick={handleDownload}
            variant="outline"
            className="border-[#E7E5DF] hover:bg-[#F1EFEA] h-9"
          >
            <Download className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button
            data-testid="export-cf-csv-btn"
            onClick={handleCsvExport}
            variant="outline"
            className="border-[#E7E5DF] hover:bg-[#F1EFEA] h-9"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Avg Monthly Deposits"
          value={fmt(avg.avg_monthly_gross_deposits)}
          icon={DollarSign}
        />
        <KPICard
          title="Avg Operating Cash Flow"
          value={fmt(avg.avg_monthly_operating_cash_flow)}
          icon={TrendingUp}
          variant={avg.avg_monthly_operating_cash_flow > 0 ? "success" : "danger"}
        />
        <KPICard
          title="Free Cash Flow"
          value={fmt(avg.free_cash_flow)}
          subtitle="Available for new debt"
          icon={ShieldCheck}
          variant={avg.free_cash_flow > 5000 ? "success" : avg.free_cash_flow > 0 ? "warning" : "danger"}
        />
        <KPICard
          title="DSCR"
          value={avg.dscr >= 99 ? "N/A" : `${avg.dscr}x`}
          subtitle={avg.dscr >= 1.5 ? "Healthy" : avg.dscr >= 1.15 ? "Tight" : "At Risk"}
          icon={Activity}
          variant={avg.dscr >= 1.5 ? "success" : avg.dscr >= 1.15 ? "warning" : "danger"}
        />
      </div>

      {/* Revenue vs Expenses Chart */}
      <div className="bg-white border border-[#E7E5DF] rounded-md p-6">
        <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917] mb-4">
          Revenue &amp; Cash Flow Trends
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#78716C" }} />
            <YAxis tick={{ fontSize: 12, fill: "#78716C" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              formatter={(value) => [`$${value.toLocaleString()}`, undefined]}
              contentStyle={{ borderRadius: 6, border: "1px solid #E7E5DF", fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="Revenue" stroke="#1A362D" fill="#1A362D" fillOpacity={0.15} strokeWidth={2} />
            <Area type="monotone" dataKey="Operating CF" stroke="#2D4A3E" fill="#2D4A3E" fillOpacity={0.08} strokeWidth={2} />
            <Area type="monotone" dataKey="Net Cash Flow" stroke="#8A9A86" fill="#8A9A86" fillOpacity={0.08} strokeWidth={1.5} strokeDasharray="4 4" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Expense Breakdown Chart */}
      <div className="bg-white border border-[#E7E5DF] rounded-md p-6">
        <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917] mb-4">
          Monthly Expense Breakdown
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={expenseData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#78716C" }} />
            <YAxis tick={{ fontSize: 12, fill: "#78716C" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              formatter={(value) => [`$${value.toLocaleString()}`, undefined]}
              contentStyle={{ borderRadius: 6, border: "1px solid #E7E5DF", fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Payroll" stackId="a" fill="#E07A5F" />
            <Bar dataKey="Rent" stackId="a" fill="#E3B505" />
            <Bar dataKey="Utilities" stackId="a" fill="#78716C" />
            <Bar dataKey="Loan Payments" stackId="a" fill="#1A362D" />
            <Bar dataKey="Owner Draws" stackId="a" fill="#8A9A86" />
            <Bar dataKey="Other" stackId="a" fill="#E7E5DF" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Summary Table */}
      <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
        <div className="p-4 border-b border-[#E7E5DF]">
          <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
            Monthly Breakdown
          </h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1EFEA]">
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Month</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Deposits</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Revenue</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Expenses</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Net CF</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Operating CF</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Debt Svc</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Ending Bal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {months.map((m) => (
                <TableRow key={m.month} className="table-row-hover">
                  <TableCell className="font-['IBM_Plex_Mono'] text-sm text-[#57534E] py-4">{m.month}</TableCell>
                  <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right text-[#1C1917] py-4">{fmt(m.total_deposits)}</TableCell>
                  <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right text-[#2D4A3E] py-4">{fmt(m.true_revenue)}</TableCell>
                  <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right text-[#E07A5F] py-4">{fmt(m.total_withdrawals)}</TableCell>
                  <TableCell className={`font-['IBM_Plex_Mono'] text-sm text-right py-4 ${m.net_cash_flow >= 0 ? "text-[#2D4A3E]" : "text-[#E07A5F]"}`}>
                    {m.net_cash_flow >= 0 ? "+" : "-"}{fmt(m.net_cash_flow)}
                  </TableCell>
                  <TableCell className={`font-['IBM_Plex_Mono'] text-sm text-right py-4 ${m.operating_cash_flow >= 0 ? "text-[#2D4A3E]" : "text-[#E07A5F]"}`}>
                    {fmt(m.operating_cash_flow)}
                  </TableCell>
                  <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right text-[#1C1917] py-4">{fmt(m.loan_payments)}</TableCell>
                  <TableCell className="font-['IBM_Plex_Mono'] text-sm text-right text-[#1C1917] py-4">{fmt(m.ending_balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Averages Summary */}
      <div className="bg-white border border-[#E7E5DF] rounded-md p-6">
        <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917] mb-4">
          Summary Averages
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Avg Monthly Revenue</p>
            <p className="text-xl font-semibold text-[#1C1917] font-['IBM_Plex_Mono']">{fmt(avg.avg_monthly_revenue)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Avg Monthly Expenses</p>
            <p className="text-xl font-semibold text-[#E07A5F] font-['IBM_Plex_Mono']">{fmt(avg.avg_monthly_expenses)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Existing Debt Service</p>
            <p className="text-xl font-semibold text-[#1C1917] font-['IBM_Plex_Mono']">{fmt(avg.existing_monthly_debt_service)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Net Cash Flow</p>
            <p className={`text-xl font-semibold font-['IBM_Plex_Mono'] ${avg.avg_monthly_net_cash_flow >= 0 ? "text-[#2D4A3E]" : "text-[#E07A5F]"}`}>
              {avg.avg_monthly_net_cash_flow >= 0 ? "+" : "-"}{fmt(avg.avg_monthly_net_cash_flow)}
            </p>
          </div>
        </div>
      </div>

      {/* Detected Existing Loans */}
      {detected_loans?.length > 0 && (
        <div className="bg-white border border-[#1A362D]/20 rounded-md overflow-hidden" data-testid="detected-loans-section">
          <div className="p-4 border-b border-[#E7E5DF]">
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
              Detected Existing Loans
            </h3>
            <p className="text-xs text-[#78716C] mt-1">Recurring payment patterns detected from bank statements</p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1EFEA]">
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Payee</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Payment</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Frequency</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Monthly Equiv.</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Payments</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Period</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detected_loans.map((loan, i) => (
                  <TableRow key={i} className="table-row-hover">
                    <TableCell className="py-3 text-sm text-[#1C1917] max-w-[200px] truncate">{loan.payee}</TableCell>
                    <TableCell className="py-3 text-sm text-right font-['IBM_Plex_Mono'] text-[#E07A5F]">{fmt(loan.payment_amount)}</TableCell>
                    <TableCell className="py-3">
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-[#1A362D]/10 text-[#1A362D]">
                        {loan.frequency}
                      </span>
                    </TableCell>
                    <TableCell className="py-3 text-sm text-right font-['IBM_Plex_Mono'] font-semibold text-[#1C1917]">{fmt(loan.monthly_equivalent)}</TableCell>
                    <TableCell className="py-3 text-sm text-right font-['IBM_Plex_Mono'] text-[#57534E]">{loan.payment_count}</TableCell>
                    <TableCell className="py-3 text-xs text-[#78716C] font-['IBM_Plex_Mono']">
                      {loan.first_payment?.slice(0, 7)} — {loan.last_payment?.slice(0, 7)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="p-4 border-t border-[#E7E5DF] bg-[#F1EFEA]">
            <p className="text-sm font-semibold text-[#1C1917]">
              Total Detected Monthly Debt: <span className="font-['IBM_Plex_Mono'] text-[#E07A5F]">{fmt(report.total_detected_monthly_debt)}</span>
            </p>
          </div>
        </div>
      )}

      {/* Red Flags */}
      {red_flags.length > 0 && (
        <div className="bg-white border border-[#E07A5F]/30 rounded-md p-6" data-testid="red-flags-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#E07A5F]" />
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#E07A5F]">
              Red Flags
            </h3>
          </div>
          <ul className="space-y-2">
            {red_flags.map((flag, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#57534E]">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E07A5F] flex-shrink-0" />
                {flag}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
